import { useMemo } from "react";
import { generatePath } from "react-router";

import { formatDate, formatUnixTimeToDate } from "@/lib/dayjs/format-date";
import type { AirHostReservationSyncJob } from "@/lib/gql/__generated__/graphql";
import { AirHostReservationSyncJobStatus } from "@/lib/gql/__generated__/graphql";

import { PATHS } from "@/routes/paths";

import { Button } from "@/components/ui/button/button";
import type { LabelProps } from "@/components/ui/label/label";
import { Label } from "@/components/ui/label/label";

type Props = {
	reservationSyncJob: AirHostReservationSyncJob;
};

const STATUS_LABELS: Record<AirHostReservationSyncJobStatus, string> = {
	[AirHostReservationSyncJobStatus.Pending]: "待機中",
	[AirHostReservationSyncJobStatus.InProgress]: "処理中",
	[AirHostReservationSyncJobStatus.Completed]: "完了",
	[AirHostReservationSyncJobStatus.PartiallyCompleted]: "完了（連携できなかった予約がある）",
};

const STATUS_VARIANTS: Record<AirHostReservationSyncJobStatus, LabelProps["variant"]> = {
	[AirHostReservationSyncJobStatus.Pending]: "black",
	[AirHostReservationSyncJobStatus.InProgress]: "blue",
	[AirHostReservationSyncJobStatus.Completed]: "green",
	[AirHostReservationSyncJobStatus.PartiallyCompleted]: "required",
};

export const SettingsSiteControllerAirHostReservationSyncJobSection = ({ reservationSyncJob }: Props) => {
	const hasSkippedReservations = useMemo(
		() => reservationSyncJob.skippedReservations.length > 0,
		[reservationSyncJob.skippedReservations],
	);

	const hasFailedReservations = useMemo(
		() => reservationSyncJob.failedReservations.length > 0,
		[reservationSyncJob.failedReservations],
	);

	const handleOpenReservation = (reservationID: string) => {
		const path = generatePath(PATHS.HOTEL.MANAGEMENT.RESERVATION.DETAIL, {
			hotelID: reservationSyncJob.hotelID,
			reservationID,
		});
		window.open(path, "_blank");
	};

	return (
		<div className="space-y-8">
			{/* ステータス表示 */}
			<div className="flex items-center gap-x-4">
				<span className="text-sm font-medium text-zinc-700">ステータス:</span>
				<Label
					text={STATUS_LABELS[reservationSyncJob.status]}
					variant={STATUS_VARIANTS[reservationSyncJob.status]}
				/>
			</div>

			{/* 予約数サマリー */}
			<div className="space-y-2">
				<p>
					連携対象期間：{formatDate(reservationSyncJob.syncTargetStartDate, "YYYY年MM月DD日")} 〜{" "}
					{formatDate(reservationSyncJob.syncTargetEndDate, "YYYY年MM月DD日")}
				</p>

				<div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
					<div className="rounded-lg border border-zinc-200 p-4">
						<p className="text-sm text-zinc-500">総予約数</p>
						<p className="text-2xl font-semibold text-zinc-900">{reservationSyncJob.totalReservations}</p>
					</div>
					<div className="border-green-200 bg-green-50 rounded-lg border p-4">
						<p className="text-sm text-green-600">連携済み</p>
						<p className="text-2xl font-semibold">{reservationSyncJob.syncedReservations}</p>
					</div>
					<div className="rounded-lg border p-4">
						<p className="text-sm text-yellow-600">スキップ</p>
						<p className=" text-2xl font-semibold">{reservationSyncJob.skippedReservations.length}</p>
					</div>
					<div className="rounded-lg border p-4">
						<p className="text-sm text-error-600">失敗</p>
						<p className="text-2xl font-semibold">{reservationSyncJob.failedReservations.length}</p>
					</div>
				</div>
			</div>

			{/* スキップされた予約ID一覧 */}
			{hasSkippedReservations && (
				<div className="space-y-2">
					<h4 className="text-sm font-medium text-zinc-700">
						スキップされた予約（紐付け設定されていない部屋の予約）
					</h4>
					<div className="flex flex-wrap gap-2">
						{reservationSyncJob.skippedReservations.map((skippedReservation) => (
							<Button
								variant="input-ish"
								size="sm"
								key={skippedReservation.reservationID}
								onClick={() => handleOpenReservation(skippedReservation.reservationID)}
							>
								{skippedReservation.reservationID}
							</Button>
						))}
					</div>
				</div>
			)}

			{/* 失敗した予約ID一覧 */}
			{hasFailedReservations && (
				<div className="space-y-2">
					<h4 className="text-sm font-medium text-zinc-700">連携に失敗した予約</h4>
					<div className="flex flex-wrap gap-2">
						{reservationSyncJob.failedReservations.map((failedReservation) => (
							<Button
								variant="input-ish"
								size="lg"
								key={failedReservation.reservationID}
								onClick={() => handleOpenReservation(failedReservation.reservationID)}
							>
								<span className="w-32">{failedReservation.reservationID}</span>（
								<p className="rounded bg-zinc-100 p-0.5 text-xs">{failedReservation.error}</p>）
							</Button>
						))}
					</div>
				</div>
			)}

			{/* 日時情報 */}
			<div className="flex flex-wrap gap-x-6 gap-y-1 border-t border-zinc-200 pt-4 text-xs">
				<span>
					連携開始：{formatUnixTimeToDate(reservationSyncJob.createdAt, "YYYY年MM月DD日（dd） HH時mm分ss秒")}
				</span>
				<span>
					連携終了：{formatUnixTimeToDate(reservationSyncJob.updatedAt, "YYYY年MM月DD日（dd） HH時mm分ss秒")}
				</span>
			</div>

			<p className="text-xs text-zinc-500">※最新の情報は画面を再読み込みすると反映されます</p>
		</div>
	);
};

SettingsSiteControllerAirHostReservationSyncJobSection.displayName =
	"SettingsSiteControllerAirHostReservationSyncJobSection";
